import React, { useEffect, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html, QuadraticBezierLine } from '@react-three/drei';
import * as THREE from 'three';
import type { HeadId, Vec3 } from '../data/content';
import { formatPct } from '../lib/model';
import { ARC_Y, bezier } from '../lib/layout';
import { useLabelFactor } from './sceneHooks';
type Props = {
  xs: number[];
  colors: string[];
  selectedIndex: number;
  weights: number[];
  head: HeadId;
  reducedMotion: boolean;
};
const DOT_GEO = new THREE.SphereGeometry(0.05, 12, 12);
export function AttentionArcs({
  xs,
  colors,
  selectedIndex,
  weights,
  head,
  reducedMotion
}: Props) {
  const i = selectedIndex;
  const labelFactor = useLabelFactor();
  const lineRefs = useRef<(THREE.Mesh | null)[]>([]);
  const dotRefs = useRef<(THREE.Mesh | null)[]>([]);
  const fade = useRef(0);
  useEffect(() => {
    fade.current = 0;
  }, [i, head]);
  const arcs = weights.map((w, j) => {
    const start: Vec3 = [xs[j], ARC_Y, 0];
    const end: Vec3 = [xs[i], ARC_Y, 0];
    const mid: Vec3 = [(xs[j] + xs[i]) / 2, ARC_Y + 0.6 + 0.3 * Math.abs(xs[i] - xs[j]), 0];
    return {
      j,
      w,
      start,
      mid,
      end,
      peak: bezier(start, mid, end, 0.5)
    };
  }).filter(arc => arc.j !== i);
  const selfWeight = weights[i] ?? 0;
  useFrame((state, delta) => {
    fade.current = Math.min(1, fade.current + Math.min(delta, 0.05) * 2.5);
    const f = fade.current;
    const t = state.clock.elapsedTime;
    arcs.forEach((arc, n) => {
      const line = lineRefs.current[n];
      if (line) (line.material as THREE.Material).opacity = (0.15 + 0.85 * arc.w) * f;
      const dot = dotRefs.current[n];
      if (dot) {
        const u = reducedMotion ? 0.5 : (t * 0.5 + arc.j * 0.17) % 1;
        const p = bezier(arc.start, arc.mid, arc.end, u);
        dot.position.set(p[0], p[1], p[2]);
        dot.scale.setScalar((0.4 + 1.6 * Math.sqrt(arc.w)) * f);
      }
    });
  });
  return <group data-id="element-17" data-mp="1u1i06p0s35cq">
      {arcs.map((arc, n) => <group key={`${arc.j}-${i}`} data-id="element-18" data-mp="3u2eswg1fow9j" data-mpi={n}>
          <QuadraticBezierLine ref={el => {
        lineRefs.current[n] = el as unknown as THREE.Mesh | null;
      }} start={arc.start} end={arc.end} mid={arc.mid} color={colors[arc.j]} lineWidth={1 + arc.w * 7} transparent opacity={0} depthWrite={false} data-id="element-19" data-mp="3u3himkn0ssnv" />
          <mesh ref={el => {
        dotRefs.current[n] = el;
      }} geometry={DOT_GEO} scale={0.0001} data-id="element-20" data-mp="gul4nmksjnlc">
            <meshBasicMaterial color={colors[arc.j]} toneMapped={false} data-id="element-21" data-mp="qkeiyvcbigta" />
          </mesh>
          {arc.w >= 0.06 && <Html center position={[arc.peak[0], arc.peak[1] + 0.2, 0]} distanceFactor={9 * labelFactor} zIndexRange={[10, 0]} wrapperClass="pointer-events-none" className="pointer-events-none" data-id="element-22" data-mp="vj7w2mnp05sx">
              <span className="select-none whitespace-nowrap rounded-full border border-white/10 bg-[#0B0C12]/90 px-2 py-0.5 font-mono text-[14px] font-medium animate-[fadeIn_0.5s_ease_both]" style={{
          color: colors[arc.j]
        }} data-id="element-23" data-mp="2p39hfa4irkjq">
                {formatPct(arc.w)}
              </span>
            </Html>}
        </group>)}

      <mesh position={[xs[i], ARC_Y + 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]} data-id="element-24" data-mp="37t7slz3m5trl">
        <torusGeometry args={[0.3, 0.015 + selfWeight * 0.05, 8, 48]} data-id="element-25" data-mp="33qukx55b3ls6" />
        <meshBasicMaterial color={colors[i]} transparent opacity={0.35 + 0.65 * selfWeight} toneMapped={false} data-id="element-26" data-mp="33tqjmvozaeho" />
      </mesh>
      <Html center position={[xs[i], ARC_Y + 0.42, 0]} distanceFactor={9 * labelFactor} zIndexRange={[10, 0]} wrapperClass="pointer-events-none" className="pointer-events-none" data-id="element-27" data-mp="2u11iour20j0l">
        <span className="select-none whitespace-nowrap rounded-full border border-white/10 bg-[#0B0C12]/90 px-2 py-0.5 font-mono text-[13px] text-[#9A9FB0]" data-id="element-28" data-mp="36apm8mr5ip2q">
          self {formatPct(selfWeight)}
        </span>
      </Html>
    </group>;
}